import { useQuery } from '@apollo/client';
import InfoIcon from '@atlaskit/icon/glyph/info';
import Tooltip from '@atlaskit/tooltip';
import {
  DynamicResultTable,
  FormattedDate,
  useNotification,
  successNotificationBuilder,
  NotificationTypes,
  useFeatureToggle,
} from '@css-xp/base';
import { CustomSearch } from '@css-xp/base/custom-search';
import { generatedTypesNew, Maybe } from '@css-xp/graphtypes';
import { NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
import React, { useCallback, useState } from 'react';
import './generating-invoice-tab.scss';
import { fetchPendingInvoicesForInvoiceGroup } from './queries/fetch-pendingInvoices';
import { generateInvoices } from '@atlassian/temp-proxies';
import { ENABLE_GENERATE_INVOICES } from '@atlassian/cssxp-utils-generic';
import LoadingButton from '@atlaskit/button/loading-button';

interface Props {
  label: string;
  invoiceGroupId: string;
  transactionAccountId: string;
  showFilters?: boolean;
}

interface FormData {
  searchTerm: string;
}
export const emptyStateProp = {
  header: '',
  description: NO_DETAILS_TO_SHOW,
  imageUrl: '',
};

const DEFAULT_ROWS_PER_PAGE = 8;
const DEFAULT_PAGE = 1;

const dateFormatForGeneratedInvoice = 'MMM DD, YYYY - hh:mm A z';

function getDataForCurrentPage(currentPage: number, pageSize: number, data: Maybe<number>[]) {
  if (!data || data.length === 0) return [];
  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  return data?.slice(start, end);
}

const getContentToGenerateInvoice = () => {
  return (
    <div className="generate-invoice-content-wrapper">
      <span>Invoice will be ready by</span>
      <span className="generate-invoice-tooltip-wrapper">
        <Tooltip content={<>It takes up to 24 hours to generate an invoice</>} position="bottom-start" tag="span">
          <InfoIcon size="small" label="pending-invoice-info" />
        </Tooltip>
      </span>
    </div>
  );
};

export const GeneratingInvoices = ({ label, invoiceGroupId, transactionAccountId, showFilters = false }: Props) => {
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
  const [visiblePendingInvoices, setVisiblePendingInvoices] = useState<Maybe<number>[]>();
  const [generatingInvoices, setGeneratingInvoices] = useState<boolean>(false);

  const { sendSuccessNotification, setNotificationSent } = useNotification();

  const [isEnabled] = useFeatureToggle();
  const isGeneratingInvoicesEnabled = isEnabled(ENABLE_GENERATE_INVOICES);

  const { data, loading, refetch } = useQuery<
    generatedTypesNew.FetchPendingInvoicesForInvoiceGroupQuery,
    generatedTypesNew.FetchPendingInvoicesForInvoiceGroupQueryVariables
  >(fetchPendingInvoicesForInvoiceGroup, {
    variables: {
      id: invoiceGroupId,
    },
  });

  React.useEffect(() => {
    if (data && !loading) {
      setVisiblePendingInvoices(
        getDataForCurrentPage(currentPage, rowsPerPage, data?.invoiceGroup?.pendingInvoices ?? [])
      );
    }
  }, [data]);

  const pendingInvoices = data?.invoiceGroup?.pendingInvoices ?? [];

  const totalCount = pendingInvoices && pendingInvoices.length;
  const onSearch = async (formData: FormData) => {
    const input = formData && formData.searchTerm;
    const result = await refetch();
    return;
  };

  const getFormattedDate = (date: number) => {
    const timeStampAfterOneDay = (date + 24 * 60 * 60 * 1000).toString();
    return (
      <div style={{ margin: '15px auto' }}>
        <FormattedDate date={timeStampAfterOneDay} showTime format={dateFormatForGeneratedInvoice} />
      </div>
    );
  };

  const createRows = () => {
    return (visiblePendingInvoices || [])
      .filter((date) => date)
      .map((date, index) => {
        return {
          cells: [
            {
              key: `${label}` + index,
              content: date ? getFormattedDate(date) : '',
            },
          ],
        };
      });
  };

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      setVisiblePendingInvoices(getDataForCurrentPage(page, rowsPerPage, pendingInvoices));
    },
    [currentPage, visiblePendingInvoices]
  );

  const handleRowsChange = useCallback(
    (rowsCount: number) => {
      setCurrentPage(1);
      setRowsPerPage(rowsCount);
      setVisiblePendingInvoices(getDataForCurrentPage(1, rowsCount, pendingInvoices));
    },
    [rowsPerPage, visiblePendingInvoices]
  );

  const handleGenerateInvoices = useCallback(async () => {
    setGeneratingInvoices(true);
    const invoiceResponse = await generateInvoices({
      invoiceGroupId: invoiceGroupId,
      transactionAccountId: transactionAccountId,
    });
    setGeneratingInvoices(false);
    if (invoiceResponse) {
      const notificationOptions = successNotificationBuilder()
        ?.withId('generate-invoices-notification')
        .withHeader('Invoices generated successfully.')
        .withDismissAfter(5000)
        .isVersion(NotificationTypes.FLAG)
        .build();
      sendSuccessNotification(notificationOptions);
      setNotificationSent(true);
      refetch();
    }
  }, [invoiceGroupId, transactionAccountId, setNotificationSent, refetch, sendSuccessNotification]);

  const renderActionsOnTop = useCallback(() => {
    if (totalCount > 0 && isGeneratingInvoicesEnabled) {
      return [
        () => (
          <LoadingButton
            isDisabled={generatingInvoices}
            isLoading={generatingInvoices}
            onClick={handleGenerateInvoices}
          >
            Generate invoices
          </LoadingButton>
        ),
      ];
    }
    return undefined;
  }, [handleGenerateInvoices, totalCount, isGeneratingInvoicesEnabled, generatingInvoices]);

  return (
    <DynamicResultTable
      testId="generating-Invoice"
      head={{
        cells: [],
      }}
      rows={[...createRows()]}
      emptyStateProps={emptyStateProp}
      isLoading={loading}
      isPaginationEnabled={totalCount > rowsPerPage}
      totalCount={totalCount}
      onPageChange={handlePageChange}
      rowsPerPage={DEFAULT_ROWS_PER_PAGE}
      currentPage={currentPage}
      onRowChange={handleRowsChange}
      actions={renderActionsOnTop()}
      showFilters={showFilters}
    >
      <div className="generate-invoice-header-container">
        <CustomSearch placeholder={'Search all generating invoices'} onSearch={onSearch} isLoading={loading} />
        {getContentToGenerateInvoice()}
      </div>
    </DynamicResultTable>
  );
};
